import React, { useState, useEffect } from 'react';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import {
    Plus,
    Calendar,
    AlertCircle,
    MapPin,
    Package,
    Truck,
    CheckCircle2,
    Trash2,
    AlertTriangle,
    History,
    FileText,
    History as HistoryIcon,
    Edit,
    MoreVertical,
    XCircle,
    ArrowLeft
} from 'lucide-react';
import NewIncomingOrder from './NewIncomingOrder';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { cn } from '../../components/ui/utils';
import { toast } from 'sonner';
import { toSydneyDate, formatTimestamp } from '../../utils/helpers';

interface OrderItem {
    id: string;
    quantityOrdered: number;
    quantityReceived: number;
    status: 'Pending' | 'Partially Received' | 'Received';
    inventoryItem: {
        id: string;
        name: string;
        sku: string;
    };
    order: {
        id: string;
        poNumber: string;
        expectedDeliveryDate: string;
        status: string;
        createdAt: string;
        updatedBy?: { firstName: string, lastName: string };
        updatedAt?: string;
        supplier: { name: string };
        deliveryLocation: { name: string };
    };
}

const IncomingInventory: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const [incomingItems, setIncomingItems] = useState<OrderItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<'list' | 'add' | 'edit'>('list');
    const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<OrderItem | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
    const [isCloseShortModalOpen, setIsCloseShortModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pendingReceipt, setPendingReceipt] = useState<any | null>(null);

    useEffect(() => {
        setCurrentPage(1); // Reset page when switching tabs
        fetchIncomingItems(1);
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'completed') {
            fetchIncomingItems(currentPage);
        }
    }, [currentPage]);

    const fetchIncomingItems = async (pageToFetch?: number) => {
        try {
            setLoading(true);
            const page = pageToFetch || currentPage;
            const data = await api.get<any>(`/api/inventory/orders/items?type=${activeTab}${activeTab === 'completed' ? `&page=${page}&limit=10` : ''}`);

            if (activeTab === 'completed' && data.items) {
                setIncomingItems(data.items);
                setTotalPages(data.totalPages);
            } else {
                setIncomingItems(Array.isArray(data) ? data : []);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error fetching incoming inventory:', error);
            setIncomingItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOrder = async () => {
        if (!selectedOrder) return;
        try {
            await api.delete(`/api/inventory/orders/${selectedOrder.id}`);
            toast.success('Order deleted successfully');
            fetchIncomingItems();
            setIsDeleteModalOpen(false);
            setSelectedOrder(null);
        } catch (error: any) {
            toast.error(error.message || 'Failed to delete order');
        }
    };

    const handleCloseShort = async () => {
        if (!selectedOrder) return;
        try {
            if (pendingReceipt && pendingReceipt.quantityReceived > 0) {
                // If there's a pending quantity in the modal, process it AND close the order
                await api.post(`/api/inventory/orders/items/${selectedItem?.id}/receive`, {
                    ...pendingReceipt,
                    closeShort: true
                });
            } else {
                // Otherwise just close the order short
                await api.post(`/api/inventory/orders/${selectedOrder.id}/close-short`, {});
            }
            toast.success('Order closed short successfully');
            fetchIncomingItems();
            setIsCloseShortModalOpen(false);
            setIsReceiveModalOpen(false);
            setSelectedOrder(null);
            setPendingReceipt(null);
        } catch (error: any) {
            toast.error(error.message || 'Failed to close order short');
        }
    };

    const isOverdue = (date: string) => {
        return new Date(date) < new Date();
    };


    if (view === 'add' || view === 'edit') {
        return (
            <NewIncomingOrder
                onBack={() => {
                    setView('list');
                    setSelectedOrder(null);
                }}
                onRefresh={fetchIncomingItems}
                order={view === 'edit' ? selectedOrder : null}
            />
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="bg-white p-1 rounded-2xl border border-gray-100 shadow-sm flex gap-1">
                        <button
                            onClick={() => setActiveTab('active')}
                            className={cn(
                                "px-6 py-2 rounded-xl text-xs font-bold transition-all",
                                activeTab === 'active'
                                    ? "bg-black text-white shadow-md shadow-black/10"
                                    : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                            )}
                        >
                            Active Orders
                        </button>
                        <button
                            onClick={() => setActiveTab('completed')}
                            className={cn(
                                "px-6 py-2 rounded-xl text-xs font-bold transition-all",
                                activeTab === 'completed'
                                    ? "bg-black text-white shadow-md shadow-black/10"
                                    : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                            )}
                        >
                            History
                        </button>
                    </div>
                </div>
                {ability.can(ACTIONS.CREATE, RESOURCES.INCOMING_INVENTORY) && (
                    <Button
                        onClick={() => setView('add')}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 bg-black hover:bg-slate-800 text-white rounded-xl shadow-lg border-0 transition-all active:scale-95"
                    >
                        <Plus size={18} />
                        Add Incoming Order
                    </Button>
                )}
            </div>

            <div className="space-y-4">
                {/* Desktop view */}
                <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <Table>
                        <TableHeader className="bg-gray-50/50">
                            <TableRow>
                                <TableHead className="font-bold text-gray-400">Item Name</TableHead>
                                <TableHead className="font-bold text-gray-400">Ordered On</TableHead>
                                <TableHead className="font-bold text-gray-400">Quantity Ordered</TableHead>
                                <TableHead className="font-bold text-gray-400">ETA</TableHead>
                                <TableHead className="font-bold text-gray-400">Status</TableHead>
                                <TableHead className="font-bold text-gray-400">Supplier</TableHead>
                                <TableHead className="font-bold text-gray-400">Location</TableHead>
                                <TableHead className="w-[180px] text-right font-bold text-gray-400 pr-6">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                [...Array(3)].map((_, i) => (
                                    <TableRow key={i} className="animate-pulse">
                                        <TableCell><div className="h-10 bg-gray-100 rounded-lg w-40" /></TableCell>
                                        <TableCell><div className="h-4 bg-gray-100 rounded-full w-24" /></TableCell>
                                        <TableCell><div className="h-4 bg-gray-100 rounded-full w-12" /></TableCell>
                                        <TableCell><div className="h-4 bg-gray-100 rounded-full w-24" /></TableCell>
                                        <TableCell><div className="h-6 bg-gray-100 rounded-full w-20" /></TableCell>
                                        <TableCell><div className="h-4 bg-gray-100 rounded-full w-32" /></TableCell>
                                        <TableCell><div className="h-4 bg-gray-100 rounded-full w-32" /></TableCell>
                                        <TableCell />
                                    </TableRow>
                                ))
                            ) : incomingItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="h-64 text-center">
                                        <div className="flex flex-col items-center justify-center text-gray-400">
                                            {activeTab === 'active' ? (
                                                <>
                                                    <Truck size={48} className="mb-4 opacity-20" />
                                                    <p className="text-lg font-medium">No pending incoming orders</p>
                                                </>
                                            ) : (
                                                <>
                                                    <HistoryIcon size={48} className="mb-4 opacity-20" />
                                                    <p className="text-lg font-medium">No completed orders found</p>
                                                </>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                incomingItems.map((item) => (
                                    <TableRow key={item.id} className="group hover:bg-gray-50/50 transition-colors border-b border-gray-50 last:border-0">
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className={cn(
                                                    "w-10 h-10 rounded-xl flex items-center justify-center border border-gray-100",
                                                    item.inventoryItem.name.includes('Vest') ? "bg-orange-50" : "bg-blue-50"
                                                )}>
                                                    <Package size={18} className={item.inventoryItem.name.includes('Vest') ? "text-orange-500" : "text-blue-500"} />
                                                </div>
                                                <div className="space-y-0.5">
                                                    <p className="font-bold text-sm text-gray-800">{item.inventoryItem.name}</p>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                                        PO: {item.order.poNumber}
                                                    </p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2 text-sm font-bold text-gray-600">
                                                <Calendar size={14} className="text-blue-400" />
                                                {toSydneyDate(item.order.createdAt)}
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-bold text-sm text-gray-600">{item.quantityOrdered}</TableCell>
                                        <TableCell>
                                            <div className={cn(
                                                "flex items-center gap-2 text-sm font-bold",
                                                activeTab === 'active' && isOverdue(item.order.expectedDeliveryDate) ? "text-red-500" : "text-gray-600"
                                            )}>
                                                <Calendar size={14} />
                                                {toSydneyDate(item.order.expectedDeliveryDate)}
                                                {activeTab === 'active' && isOverdue(item.order.expectedDeliveryDate) && <AlertTriangle size={14} />}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={cn(
                                                "rounded-lg font-bold text-[10px] px-2.5 py-1 border-0 tracking-wider",
                                                activeTab === 'active' && isOverdue(item.order.expectedDeliveryDate) ? "bg-red-50 text-red-500" :
                                                    item.order.status === 'Completed (Closed Short)' ? "bg-orange-50 text-orange-600" :
                                                        item.order.status === 'Received' ? "bg-green-50 text-green-600" :
                                                            "bg-blue-50 text-blue-600"
                                            )}>
                                                {activeTab === 'active' && isOverdue(item.order.expectedDeliveryDate) ? "Overdue" : item.order.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm font-medium text-gray-600">{item.order.supplier.name}</TableCell>
                                        <TableCell className="text-sm font-medium text-gray-600">{item.order.deliveryLocation.name}</TableCell>
                                        <TableCell className="text-right pr-6">
                                            <div className="flex items-center justify-end gap-2">
                                                {activeTab === 'active' && (
                                                    <>
                                                        {ability.can(ACTIONS.UPDATE, RESOURCES.INCOMING_INVENTORY) && (
                                                            <Button
                                                                variant="outline"
                                                                size="icon"
                                                                onClick={() => {
                                                                    setSelectedOrder(item.order);
                                                                    setView('edit');
                                                                }}
                                                                className="w-9 h-9 rounded-xl border-gray-100 hover:bg-blue-50 hover:text-blue-600 transition-all shadow-sm"
                                                                title="Edit Order"
                                                            >
                                                                <Edit size={14} />
                                                            </Button>
                                                        )}
                                                        {ability.can(ACTIONS.DELETE, RESOURCES.INCOMING_INVENTORY) && (
                                                            <Button
                                                                variant="outline"
                                                                size="icon"
                                                                onClick={() => {
                                                                    setSelectedOrder(item.order);
                                                                    setIsDeleteModalOpen(true);
                                                                }}
                                                                className="w-9 h-9 rounded-xl border-gray-100 hover:bg-red-50 hover:text-red-600 transition-all shadow-sm"
                                                                title="Delete Order"
                                                            >
                                                                <Trash2 size={14} />
                                                            </Button>
                                                        )}
                                                        {ability.can(ACTIONS.UPDATE, RESOURCES.INCOMING_INVENTORY) && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setSelectedItem(item);
                                                                    setIsReceiveModalOpen(true);
                                                                }}
                                                                className="rounded-xl font-bold text-xs h-9 gap-2 shadow-sm whitespace-nowrap bg-slate-900 text-white hover:bg-slate-800 hover:text-white border-0"
                                                            >
                                                                <CheckCircle2 size={14} />
                                                                Receive
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                                {activeTab === 'completed' && (
                                                    <div className="flex flex-col items-end gap-1">
                                                        <div className="flex flex-col items-end">
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Received</p>
                                                            <p className={cn(
                                                                "text-sm font-bold",
                                                                item.quantityReceived < item.quantityOrdered ? "text-orange-600" : "text-green-600"
                                                            )}>
                                                                {item.quantityReceived} / {item.quantityOrdered}
                                                            </p>
                                                            {item.order.status === 'Completed (Closed Short)' && item.order.updatedBy && (
                                                                <p className="text-[9px] text-gray-400 font-medium italic mt-0.5">
                                                                    Closed by {item.order.updatedBy.firstName}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 px-2 text-[10px] font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg gap-1 border border-blue-100"
                                                            onClick={() => {
                                                                setSelectedItem(item);
                                                                setIsHistoryModalOpen(true);
                                                            }}
                                                        >
                                                            <HistoryIcon size={12} />
                                                            View Logs
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden space-y-4">
                    {loading ? (
                        [...Array(3)].map((_, i) => (
                            <div key={i} className="bg-white p-5 rounded-2xl border border-gray-100 animate-pulse space-y-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gray-100 rounded-xl" />
                                    <div className="space-y-2">
                                        <div className="h-4 bg-gray-100 rounded w-32" />
                                        <div className="h-2 bg-gray-100 rounded w-20" />
                                    </div>
                                </div>
                                <div className="h-20 bg-gray-50 rounded-xl" />
                            </div>
                        ))
                    ) : incomingItems.length === 0 ? (
                        <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-12 text-center">
                            {activeTab === 'active' ? (
                                <>
                                    <Truck size={40} className="mx-auto text-gray-300 mb-4 opacity-50" />
                                    <p className="text-gray-400 font-medium">No pending incoming orders</p>
                                </>
                            ) : (
                                <>
                                    <HistoryIcon size={40} className="mx-auto text-gray-300 mb-4 opacity-50" />
                                    <p className="text-gray-400 font-medium">No completed orders found</p>
                                </>
                            )}
                        </div>
                    ) : (
                        incomingItems.map((item) => (
                            <div key={item.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex items-center gap-3">
                                        <div className={cn(
                                            "w-10 h-10 rounded-xl flex items-center justify-center border border-gray-100",
                                            item.inventoryItem.name.includes('Vest') ? "bg-orange-50" : "bg-blue-50"
                                        )}>
                                            <Package size={18} className={item.inventoryItem.name.includes('Vest') ? "text-orange-500" : "text-blue-500"} />
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-800 line-clamp-1">{item.inventoryItem.name}</p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase">PO: {item.order.poNumber}</p>
                                                <span className="text-[10px] text-gray-300">•</span>
                                                <p className="text-[10px] font-bold text-blue-500 uppercase">Ordered: {toSydneyDate(item.order.createdAt)}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <Badge variant="outline" className={cn(
                                        "rounded-lg font-bold text-[10px] px-2.5 py-1 border-0 tracking-wider",
                                        activeTab === 'active' && isOverdue(item.order.expectedDeliveryDate) ? "bg-red-50 text-red-500" :
                                            item.order.status === 'Completed (Closed Short)' ? "bg-orange-50 text-orange-600" :
                                                item.order.status === 'Received' ? "bg-green-50 text-green-600" :
                                                    "bg-blue-50 text-blue-600"
                                    )}>
                                        {activeTab === 'active' && isOverdue(item.order.expectedDeliveryDate) ? "Overdue" : item.order.status}
                                    </Badge>
                                </div>

                                <div className="grid grid-cols-2 gap-4 py-3 border-y border-gray-50">
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Quantity</p>
                                        <p className="font-bold text-gray-900">{item.quantityOrdered} Ordered</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">ETA</p>
                                        <p className={cn(
                                            "font-bold text-sm",
                                            activeTab === 'active' && isOverdue(item.order.expectedDeliveryDate) ? "text-red-500" : "text-gray-600"
                                        )}>
                                            {toSydneyDate(item.order.expectedDeliveryDate)}
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-gray-400 font-medium">Supplier</span>
                                        <span className="font-bold text-gray-700">{item.order.supplier.name}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-gray-400 font-medium">Location</span>
                                        <span className="font-bold text-gray-700">{item.order.deliveryLocation.name}</span>
                                    </div>
                                </div>

                                {activeTab === 'active' ? (
                                    <>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                className="flex-1 rounded-xl font-bold h-11 bg-gray-50 border-gray-100 hover:bg-gray-100"
                                                onClick={() => {
                                                    setSelectedOrder(item.order);
                                                    setView('edit');
                                                }}
                                            >
                                                <Edit size={16} className="mr-2" />
                                                Edit
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="flex-1 rounded-xl font-bold h-11 bg-red-50 border-red-100 text-red-600 hover:bg-red-100"
                                                onClick={() => {
                                                    setSelectedOrder(item.order);
                                                    setIsDeleteModalOpen(true);
                                                }}
                                            >
                                                <Trash2 size={16} className="mr-2" />
                                                Delete
                                            </Button>
                                        </div>
                                        <Button
                                            variant="outline"
                                            className="w-full rounded-xl font-bold h-11 bg-slate-900 text-white border-0 hover:bg-slate-800"
                                            onClick={() => {
                                                setSelectedItem(item);
                                                setIsReceiveModalOpen(true);
                                            }}
                                        >
                                            <CheckCircle2 size={16} className="mr-2" />
                                            Receive Inventory
                                        </Button>
                                    </>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="bg-gray-50 p-4 rounded-xl flex flex-col space-y-2">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 text-gray-600">
                                                    <CheckCircle2 size={16} />
                                                    <span className="font-bold text-xs">Fulfillment</span>
                                                </div>
                                                <span className={cn(
                                                    "font-bold text-sm",
                                                    item.quantityReceived < item.quantityOrdered ? "text-orange-600" : "text-green-600"
                                                )}>
                                                    {item.quantityReceived} / {item.quantityOrdered}
                                                </span>
                                            </div>
                                            {item.order.status === 'Completed (Closed Short)' && item.order.updatedBy && (
                                                <div className="text-[10px] text-gray-400 font-medium flex items-center justify-between border-t border-gray-100 pt-2">
                                                    <span>Closed Short by</span>
                                                    <span className="font-bold text-gray-500">{item.order.updatedBy.firstName} {item.order.updatedBy.lastName}</span>
                                                </div>
                                            )}
                                        </div>
                                        <Button
                                            variant="outline"
                                            className="w-full rounded-xl font-bold h-11 bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100"
                                            onClick={() => {
                                                setSelectedItem(item);
                                                setIsHistoryModalOpen(true);
                                            }}
                                        >
                                            <HistoryIcon size={16} className="mr-2" />
                                            View Receipt History
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Pagination Controls */}
                {activeTab === 'completed' && totalPages > 1 && (
                    <div className="flex items-center justify-between px-2 pt-4 pb-2">
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                            Page {currentPage} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1 || loading}
                                className="rounded-xl h-9 px-4 font-bold border-gray-100 disabled:opacity-30"
                            >
                                Previous
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages || loading}
                                className="rounded-xl h-9 px-4 font-bold bg-black text-white hover:bg-slate-800 hover:text-white border-0 disabled:opacity-30"
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {isReceiveModalOpen && selectedItem && (
                <ReceiveModal
                    isOpen={isReceiveModalOpen}
                    onClose={() => setIsReceiveModalOpen(false)}
                    item={selectedItem}
                    onRefresh={fetchIncomingItems}
                    onCloseShort={(data?: any) => {
                        setPendingReceipt(data);
                        setSelectedOrder(selectedItem.order);
                        setIsCloseShortModalOpen(true);
                    }}
                />
            )}
            {isCloseShortModalOpen && selectedOrder && (
                <DeleteConfirmModal
                    isOpen={isCloseShortModalOpen}
                    onClose={() => setIsCloseShortModalOpen(false)}
                    onConfirm={handleCloseShort}
                    title="Close Order Short"
                    message="You're closing this order short, the order will have a status Completed (Closed Short). This cannot be undone."
                    confirmText="Close Short"
                    type="warning"
                />
            )}
            {isDeleteModalOpen && selectedOrder && (
                <DeleteConfirmModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => {
                        setIsDeleteModalOpen(false);
                        setSelectedOrder(null);
                    }}
                    onConfirm={handleDeleteOrder}
                    title="Delete Incoming Order"
                    message={`Are you sure you want to delete PO ${selectedOrder.poNumber}? This will remove the order and all its items. This action cannot be undone.`}
                />
            )}
            {isHistoryModalOpen && selectedItem && (
                <ReceiptHistoryModal
                    isOpen={isHistoryModalOpen}
                    onClose={() => {
                        setIsHistoryModalOpen(false);
                        setSelectedItem(null);
                    }}
                    item={selectedItem}
                />
            )}
        </div>
    );
};

// --- Receipt History Only Modal ---
const ReceiptHistoryModal = ({ isOpen, onClose, item }: any) => {
    const [history, setHistory] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen && item.id) {
            fetchHistory();
        }
    }, [isOpen, item.id]);

    const fetchHistory = async () => {
        try {
            setLoading(true);
            const data = await api.get<any[]>(`/api/inventory/orders/items/${item.id}/receipt-history`);
            setHistory(data);
        } catch (error) {
            console.error('Error fetching receipt history:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-3xl p-6 max-h-[90vh] overflow-y-auto">
                <DialogHeader className="flex flex-row items-center justify-between space-y-0">
                    <DialogTitle className="text-xl font-bold flex items-center gap-2">
                        <HistoryIcon size={20} className="text-blue-500" />
                        Receipt History
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                    <div className="bg-gray-50/50 p-4 rounded-2xl border border-gray-100">
                        <h4 className="font-bold text-gray-800 text-sm">{item.inventoryItem.name}</h4>
                        <div className="flex items-center justify-between mt-1">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                PO: {item.order.poNumber}
                            </p>
                            <Badge variant="outline" className={cn(
                                "text-[9px] font-bold border-0",
                                item.quantityReceived < item.quantityOrdered ? "bg-orange-50 text-orange-600" : "bg-green-50 text-green-600"
                            )}>
                                {item.quantityReceived} / {item.quantityOrdered} Total Received
                            </Badge>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <div key={i} className="h-16 bg-gray-50 animate-pulse rounded-xl" />
                            ))
                        ) : history.length === 0 ? (
                            <div className="text-center py-8 text-gray-400">
                                <p className="text-sm font-medium">No receipt logs found</p>
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-2 custom-scrollbar">
                                {history.map((receipt) => (
                                    <div key={receipt.id} className="bg-white p-3 rounded-xl border border-gray-100 flex items-center justify-between text-sm shadow-sm">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <p className="font-bold text-gray-700">{receipt.quantity} units</p>
                                                <span className={cn(
                                                    "text-[10px] px-1.5 py-0.5 rounded-md font-bold",
                                                    receipt.condition === 'Excellent' ? "bg-green-50 text-green-600" :
                                                        receipt.condition === 'Good' ? "bg-blue-50 text-blue-600" :
                                                            receipt.condition === 'Fair' ? "bg-orange-50 text-orange-600" :
                                                                receipt.condition === 'Poor' ? "bg-red-50 text-red-600" :
                                                                    "bg-gray-50 text-gray-400"
                                                )}>
                                                    {receipt.condition || 'N/A'}
                                                </span>
                                            </div>
                                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                                                Received by {receipt.performedBy?.name} • {toSydneyDate(receipt.createdAt)}
                                            </p>
                                            <p className="text-[10px] text-gray-500 bg-gray-50/50 p-1.5 rounded-lg mt-1 border border-gray-100/50 italic">
                                                "{receipt.notes || 'No receipt notes'}"
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-8">
                    <Button variant="outline" className="w-full rounded-2xl h-11 font-bold bg-black text-white hover:bg-slate-800 border-0" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// --- Receive Inventory Modal ---
const ReceiveModal = ({ isOpen, onClose, item, onRefresh, onCloseShort }: any) => {
    const [formData, setFormData] = useState<any>({
        quantityReceived: item.quantityOrdered - item.quantityReceived,
        condition: 'Excellent',
        notes: ''
    });
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    useEffect(() => {
        if (isOpen && item.id) {
            fetchHistory();
        }
    }, [isOpen, item.id]);

    const fetchHistory = async () => {
        try {
            setHistoryLoading(true);
            const data = await api.get<any[]>(`/api/inventory/orders/items/${item.id}/receipt-history`);
            setHistory(data);
        } catch (error) {
            console.error('Error fetching receipt history:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    const isValidQuantity = typeof formData.quantityReceived === 'number' && formData.quantityReceived >= 0;

    const handleReceive = async () => {
        try {
            setLoading(true);
            if (!isValidQuantity || formData.quantityReceived === 0) {
                toast.error('Please enter a quantity greater than zero to receive stock');
                return;
            }
            await api.post(`/api/inventory/orders/items/${item.id}/receive`, formData);
            toast.success('Inventory received successfully');
            onRefresh();
            onClose();
        } catch (error) {
            console.error('Error receiving inventory:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg rounded-3xl p-0 overflow-hidden">
                <div className="p-6 max-h-[95vh] overflow-y-auto custom-scrollbar">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold">Receive Inventory</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6 mt-4">
                        {/* Item Info Card */}
                        <div className="bg-gray-50/50 p-4 rounded-2xl border border-gray-100 flex items-center gap-4">
                            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                                <Package size={24} className="text-orange-500" />
                            </div>
                            <div className="flex-1">
                                <h4 className="font-bold text-gray-800">{item.inventoryItem.name}</h4>
                                <div className="flex items-center justify-between">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                        PO: {item.order.poNumber} • Expected: {item.quantityOrdered} units
                                    </p>
                                    <Badge variant="outline" className="text-[9px] font-bold bg-white border-gray-200">
                                        {item.quantityReceived} / {item.quantityOrdered} Received
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        {/* Previous Receipts */}
                        {history.length > 0 && (
                            <div className="space-y-3 p-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50/30">
                                <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wide">
                                    <HistoryIcon size={14} className="text-blue-500" />
                                    Previous Receipts
                                </div>
                                <div className="space-y-2 max-h-32 overflow-y-auto pr-2 custom-scrollbar">
                                    {history.map((receipt) => (
                                        <div key={receipt.id} className="bg-white/50 p-2.5 rounded-xl border border-gray-100 flex items-center justify-between text-sm transition-all hover:border-blue-100">
                                            <div className="space-y-0.5">
                                                <p className="font-bold text-gray-700 text-xs">{receipt.quantity} units received</p>
                                                <p className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">
                                                    {receipt.performedBy?.firstName} {receipt.performedBy?.lastName} • {formatTimestamp(receipt.createdAt)}
                                                </p>
                                            </div>
                                            <div className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg">
                                                {receipt.quantity > 0 ? `+${receipt.quantity}` : receipt.quantity}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Quantity Received Now</label>
                                <Input
                                    type="number"
                                    className="rounded-xl border-gray-100 bg-gray-50/50 h-11"
                                    value={formData.quantityReceived}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setFormData({ ...formData, quantityReceived: val === '' ? '' : parseInt(val) });
                                    }}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Condition</label>
                                <Select value={formData.condition} onValueChange={(val) => setFormData({ ...formData, condition: val })}>
                                    <SelectTrigger className="rounded-xl border-gray-100 bg-gray-50/50 h-11">
                                        <SelectValue placeholder="Select condition" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl">
                                        <SelectItem value="Excellent">Excellent</SelectItem>
                                        <SelectItem value="Good">Good</SelectItem>
                                        <SelectItem value="Fair">Fair</SelectItem>
                                        <SelectItem value="Poor">Poor</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Notes</label>
                            <textarea
                                className="w-full h-24 rounded-2xl border-gray-100 bg-gray-50/50 p-4 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all font-medium"
                                placeholder="Add any notes about the received inventory..."
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 mt-8">
                        <Button variant="outline" className="order-3 sm:order-1 flex-1 rounded-2xl h-11 font-bold" onClick={onClose}>Cancel</Button>
                        {formData.quantityReceived + item.quantityReceived < item.quantityOrdered && (
                            <Button
                                variant="outline"
                                className="order-2 sm:order-2 flex-1 rounded-2xl h-11 font-bold text-orange-600 border-orange-100 hover:bg-orange-50 whitespace-nowrap"
                                onClick={() => onCloseShort(formData)}
                                disabled={!isValidQuantity || loading}
                            >
                                Close Short
                            </Button>
                        )}
                        <Button
                            className="order-1 sm:order-3 flex-[2] rounded-2xl h-11 bg-black hover:bg-slate-800 text-white font-bold gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleReceive}
                            disabled={!isValidQuantity || loading}
                        >
                            <CheckCircle2 size={18} />
                            Receive & Update Stock
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// --- Delete Confirm Modal ---
export const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm Delete", type = "danger" }: any) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-sm rounded-[32px] p-0 overflow-hidden border-none shadow-2xl animate-in fade-in zoom-in-95">
                <div className="p-8 space-y-4 max-h-[90vh] overflow-y-auto custom-scrollbar">
                    <div className="flex flex-col items-center text-center space-y-4">
                        <div className={cn(
                            "w-20 h-20 rounded-3xl flex items-center justify-center mb-2",
                            type === "danger" ? "bg-red-50 text-red-500" : "bg-orange-50 text-orange-500"
                        )}>
                            {type === "danger" ? <XCircle size={48} strokeWidth={1.5} /> : <AlertTriangle size={48} strokeWidth={1.5} />}
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-gray-900">{title}</h3>
                            <p className="text-sm text-gray-500 mt-2 font-bold leading-relaxed px-2 uppercase tracking-tight opacity-70">{message}</p>
                        </div>
                    </div>
                    <div className="flex gap-3 mt-8">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="flex-1 rounded-2xl h-14 font-black text-[11px] uppercase tracking-wider border-gray-100 hover:bg-gray-50 transition-all"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={onConfirm}
                            className={cn(
                                "flex-1 rounded-2xl h-14 text-white font-black text-[11px] uppercase tracking-wider shadow-lg transition-all active:scale-95",
                                type === "danger" ? "bg-red-600 hover:bg-red-700 shadow-red-600/20" : "bg-orange-500 hover:bg-orange-600 shadow-orange-500/20"
                            )}
                        >
                            {confirmText}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default IncomingInventory;
